import {
	faDownload,
	faFileLines,
	faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CardChevron } from '../components/CardChevron';
import { EmptyState } from '../components/EmptyState';
import {
	HistoryFilters,
	historyRangeParamUpdates,
	resolveHistoryRange,
} from '../components/HistoryFilterBar';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { Paginator } from '../components/Paginator';
import { HistoryListSkeleton } from '../components/skeletons';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { appScrollToTop } from '../lib/appScroll';
import { customerNumberLabel } from '../lib/customerNumberLabel';
import { saveOrShareBlob } from '../lib/downloadFile';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import * as portalStatementService from '../services/portalStatementService';
import { useLoginModalStore } from '../stores/loginModalStore';

const PAGE_SIZE = 20;

/**
 * Statement history (PD-14046) — the customer's saved statements across all
 * assigned clients, modeled on the invoices page. Click into a statement for
 * the on-screen rendering and PDF download.
 */
export function StatementsPage(): JSX.Element {
	const { t } = useTranslation();
	const { token, isAuthenticated, availableClients } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
	const clientIdParam = searchParams.get('clientId') ?? '';
	// Relative range key ('' = the default, last 3 months) resolved to
	// concrete ISO bounds for the API (PD-14674). 'custom' carries explicit
	// from/to bounds (PD-14899).
	const rangeParam = searchParams.get('range') ?? '';
	const fromParam = searchParams.get('from') ?? '';
	const toParam = searchParams.get('to') ?? '';
	const { dateFrom, dateTo } = resolveHistoryRange(
		rangeParam,
		fromParam,
		toParam,
	);

	// PD-14241 — bulk zip download. With no selection the zip covers every
	// statement matching the current filters (across every assigned
	// account); checkboxes narrow it to an explicit id set.
	const [isDownloadingAll, setIsDownloadingAll] = useState(false);
	const [downloadAllError, setDownloadAllError] = useState<string | null>(null);
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [isSelectingAll, setIsSelectingAll] = useState(false);

	// A filter change redefines which statements exist, so the old
	// selection no longer describes what the user sees — reset it.
	useEffect(() => {
		setSelectedIds(new Set());
	}, [clientIdParam, rangeParam, fromParam, toParam]);

	const { data, isLoading } = useQuery({
		queryKey: [
			'portal-statements',
			currentPage,
			clientIdParam,
			rangeParam,
			fromParam,
			toParam,
		],
		queryFn: () =>
			portalStatementService.getStatements(token ?? '', {
				page: currentPage,
				limit: PAGE_SIZE,
				clientId:
					clientIdParam.length > 0 ? parseInt(clientIdParam, 10) : undefined,
				dateFrom,
				dateTo,
			}),
		enabled: token != null,
		staleTime: 30_000,
	});

	// Memoized: handleSelectPage depends on the list identity, and a fresh
	// [] every render would rebuild that callback each time.
	const statements = useMemo(() => data?.data ?? [], [data]);
	const pagination = data?.pagination ?? null;

	const updateParams = useCallback(
		(updates: Record<string, string | null>) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				for (const [key, value] of Object.entries(updates)) {
					if (value == null || value.length === 0) {
						next.delete(key);
					} else {
						next.set(key, value);
					}
				}
				return next;
			});
		},
		[setSearchParams],
	);

	const handlePageChange = useCallback(
		(page: number) => {
			updateParams({ page: page > 1 ? String(page) : null });
			appScrollToTop('smooth');
		},
		[updateParams],
	);

	const handleDownloadAll = useCallback(async () => {
		if (token == null) {
			return;
		}
		setIsDownloadingAll(true);
		setDownloadAllError(null);
		try {
			const blob = await portalStatementService.downloadStatementsZip(token, {
				clientId:
					clientIdParam.length > 0 ? parseInt(clientIdParam, 10) : undefined,
				dateFrom,
				dateTo,
				// Explicit checkbox selection; omitted = everything matching
				// the filters.
				statementIds: selectedIds.size > 0 ? [...selectedIds] : undefined,
			});
			// Local date parts (not toISOString) so the name matches the
			// user's calendar day.
			const now = new Date();
			const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
			// Browser: normal download. Native shell: write to cache + share
			// sheet — the anchor click silently no-ops in the WebView (PD-14606).
			await saveOrShareBlob(blob, `statements-${day}.zip`);
		} catch (err) {
			console.error('Error downloading statements zip:', err);
			setDownloadAllError(
				err instanceof Error && err.message !== ''
					? err.message
					: 'Download failed. Please try again.',
			);
		} finally {
			setIsDownloadingAll(false);
		}
	}, [token, clientIdParam, dateFrom, dateTo, selectedIds]);

	const toggleSelected = useCallback((id: number) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const handleSelectPage = useCallback(() => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			for (const statement of statements) {
				next.add(statement.id);
			}
			return next;
		});
	}, [statements]);

	/**
	 * Select all fetches EVERY matching id (not just the visible page), so
	 * the selection is explicit — unchecking a few afterwards still works
	 * across pages, and paging never drops what was selected.
	 */
	const handleSelectAll = useCallback(async () => {
		if (token == null) {
			return;
		}
		setIsSelectingAll(true);
		setDownloadAllError(null);
		try {
			const response = await portalStatementService.getStatementIds(token, {
				clientId:
					clientIdParam.length > 0 ? parseInt(clientIdParam, 10) : undefined,
				dateFrom,
				dateTo,
			});
			setSelectedIds(new Set(response.data));
		} catch (err) {
			console.error('Error loading statement ids for select-all:', err);
			setDownloadAllError(
				'Could not load the full statement list. Please try again.',
			);
		} finally {
			setIsSelectingAll(false);
		}
	}, [token, clientIdParam, dateFrom, dateTo]);

	const handleClearSelection = useCallback(() => {
		setSelectedIds(new Set());
	}, []);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Statements" backPath="/account" />
			<AccountBreadcrumb current="Statements" />
			{/* Header row: title + bulk download (PD-14241). The row only keeps
			    its mobile margin when the button renders — the h1 is hidden on
			    mobile, so an empty row would just push the filters down. */}
			<div
				className={`flex items-center justify-between ${
					statements.length > 0 ? 'mb-4' : 'md:mb-4'
				}`}
			>
				<h1 className="hidden md:block text-lg md:text-xl font-bold text-default">
					{t('Statements')}
				</h1>
				{statements.length > 0 && (
					<button
						type="button"
						onClick={() => {
							void handleDownloadAll();
						}}
						disabled={isDownloadingAll}
						className="ml-auto px-4 py-2 text-sm bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg min-h-touch"
					>
						{/* Swap the download glyph for a spinning icon while the
						    zip is building — the wait can be tens of seconds, so
						    motion reassures the user something is happening
						    (PD-14241). Matches the storefront's login/payment
						    button pattern (faSpinner + animate-spin). */}
						<FontAwesomeIcon
							icon={isDownloadingAll ? faSpinner : faDownload}
							className={`mr-2 ${isDownloadingAll ? 'animate-spin' : ''}`}
						/>
						{isDownloadingAll
							? t('Preparing...')
							: selectedIds.size > 0
								? `${t('Download Selected')} (${selectedIds.size})`
								: t('Download All')}
					</button>
				)}
			</div>
			{downloadAllError != null && (
				<p className="mb-4 text-sm text-danger">{t(downloadAllError)}</p>
			)}

			{/* One filter tray (date range / location) with Apply-Cancel
			    (PD-14701). No keyword search on statements. */}
			<div className="mb-4">
				<HistoryFilters
					fields={[
						{ kind: 'range', key: 'range', label: 'Date Range' },
						...(availableClients.length > 1
							? [
									{
										kind: 'location' as const,
										key: 'clientId' as const,
										label: 'Location',
										clients: availableClients,
									},
								]
							: []),
					]}
					values={{
						range: rangeParam,
						from: fromParam,
						to: toParam,
						clientId: clientIdParam,
					}}
					onApply={(next) => {
						const clientId = next.clientId;
						updateParams({
							...historyRangeParamUpdates(next),
							clientId: clientId.length > 0 ? clientId : null,
							page: null,
						});
					}}
					showActiveFilters
				/>
			</div>

			{/* Selection toolbar (PD-14241) — select the visible page or every
			    matching statement; the Download button follows the selection. */}
			{!isLoading && statements.length > 0 && (
				<div className="flex items-center flex-wrap gap-x-4 gap-y-1 mb-3 text-sm">
					<button
						type="button"
						onClick={handleSelectPage}
						className="text-portal-accent hover:underline"
					>
						{t('Select page')}
					</button>
					<button
						type="button"
						onClick={() => {
							void handleSelectAll();
						}}
						disabled={isSelectingAll}
						className="text-portal-accent hover:underline disabled:opacity-60"
					>
						{isSelectingAll
							? t('Selecting...')
							: `${t('Select all')}${pagination != null ? ` (${pagination.total})` : ''}`}
					</button>
					{selectedIds.size > 0 && (
						<>
							<span className="text-brand-muted">
								{selectedIds.size} {t('selected')}
							</span>
							<button
								type="button"
								onClick={handleClearSelection}
								className="text-portal-accent hover:underline"
							>
								{t('Clear selection')}
							</button>
						</>
					)}
				</div>
			)}

			{isLoading ? (
				/* Skeleton cards shaped like the list (PD-14692) */
				<HistoryListSkeleton />
			) : statements.length === 0 ? (
				/* Empty state (PD-14699) — filtered-empty variants first. */
				clientIdParam.length > 0 ? (
					<EmptyState
						icon={faFileLines}
						title="No statements for this location"
						hint="Try removing the location filter to see more results."
						actionLabel="Clear filter"
						onAction={() => {
							updateParams({ clientId: null, page: null });
						}}
					/>
				) : rangeParam !== 'all' ? (
					<EmptyState
						icon={faFileLines}
						title="No statements in this date range"
						hint="Older statements may fall outside the selected range."
						actionLabel="View all time"
						onAction={() => {
							updateParams({ range: 'all', from: null, to: null, page: null });
						}}
					/>
				) : (
					<EmptyState
						icon={faFileLines}
						title="No statements yet"
						hint="Your statements will appear here once they are issued."
					/>
				)
			) : (
				<div className="flex flex-col gap-3">
					{statements.map((statement) => (
						<div key={statement.id} className="flex items-center gap-3">
							{/* Checkbox sits OUTSIDE the card button — nesting an
							    input inside a button is invalid HTML (PD-14241). */}
							<input
								type="checkbox"
								checked={selectedIds.has(statement.id)}
								onChange={() => {
									toggleSelected(statement.id);
								}}
								aria-label={`${t('Select')} ${t(statement.statementNumber)}`}
								className="h-4 w-4 shrink-0 cursor-pointer"
							/>
							<button
								type="button"
								onClick={() => {
									void navigate(`/statements/${statement.id}`);
								}}
								className="flex-1 min-w-0 text-left bg-brand-surface border border-brand-border rounded-xl p-4 hover:border-portal-accent transition-colors min-h-touch"
							>
								<div className="flex items-start justify-between gap-3">
									{/* flex-1 pins the amount + chevron to the card's right
								    edge; without it justify-between floats the amount
								    mid-card at a width that varies per row (PD-14241). */}
									<div className="flex-1 min-w-0">
										<div className="font-semibold text-default">
											{t(statement.statementNumber)}
										</div>
										{/* PD-17381 — the customer number in front of the
										    account name, shown for single-account users too,
										    so the number on the card matches the statement's
										    Customer ID. */}
										<div className="text-sm text-brand-muted truncate">
											{customerNumberLabel(
												t,
												statement.customerNumber,
												statement.clientName,
											)}
										</div>
										<div className="text-sm text-brand-muted">
											{t('As of')}{' '}
											{formatStatementDate(
												statement.asOfDate as unknown as string,
											)}
										</div>
									</div>
									<div className="text-right shrink-0">
										<div className="font-bold text-default">
											{formatCurrency(statement.totalAmountDue)}
										</div>
										<div className="text-xs uppercase tracking-wide text-brand-muted">
											{t('Amount Due')}
										</div>
									</div>
									<CardChevron />
								</div>
							</button>
						</div>
					))}
				</div>
			)}

			{pagination != null && pagination.totalPages > 1 && (
				<div className="mt-4">
					<Paginator
						currentPage={pagination.page}
						totalPages={pagination.totalPages}
						onPageChange={handlePageChange}
					/>
				</div>
			)}
		</div>
	);
}
